// src/features/chales/components/AddReservationModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import toast from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { getAvailableChalesForPeriod } from "../hooks/useChalesData";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import { useClients } from "features/clients/hooks/useClients";

function AddReservationModal({
  isOpen,
  onClose,
  onAddReservation,
  availableChales: initialAvailableChales = [],
  defaultCheckInDate,
}) {
  const [availableChales, setAvailableChales] = useState(
    initialAvailableChales
  );
  const [isChecking, setIsChecking] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      checkInDate: formatDateToYYYYMMDD(defaultCheckInDate),
      checkOutDate: formatDateToYYYYMMDD(
        new Date(defaultCheckInDate.getTime() + 86400000)
      ),
      chaletNumbers: [],
      guestInfo: "",
      phoneInfo: "",
      depositDueDate: "",
      observations: "",
    },
  });

  const { clients, fetchClients, addClient } = useClients();
  const [filteredClients, setFilteredClients] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  const checkInValue = watch("checkInDate");
  const checkOutValue = watch("checkOutDate");
  const guestInfoValue = watch("guestInfo");
  const phoneInfoValue = watch("phoneInfo");

  const holidayNameCheckIn = useMemo(() => {
    if (!checkInValue) return null;
    const [year, month, day] = checkInValue.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day);
    return checkHoliday(dateObj);
  }, [checkInValue]);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen, fetchClients]);

  useEffect(() => {
    let searchTerm = "";
    let shouldSearch = false;

    if (showNameSuggestions && guestInfoValue?.length > 1) {
      searchTerm = guestInfoValue;
      shouldSearch = true;
    } else if (showPhoneSuggestions && phoneInfoValue?.length > 1) {
      searchTerm = phoneInfoValue;
      shouldSearch = true;
    }

    if (shouldSearch) {
      const lowerTerm = searchTerm.toLowerCase();
      const matches = clients.filter(
        (c) =>
          c.name.toLowerCase().includes(lowerTerm) ||
          c.phone.includes(lowerTerm) ||
          (c.cpf && c.cpf.includes(lowerTerm))
      );
      setFilteredClients(matches.slice(0, 5));
    } else {
      setFilteredClients([]);
    }
  }, [
    guestInfoValue,
    phoneInfoValue,
    showNameSuggestions,
    showPhoneSuggestions,
    clients,
  ]);

  const handleSelectClient = (client) => {
    setValue("guestInfo", client.name);
    setValue("phoneInfo", client.phone);
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const SuggestionsList = () => (
    <ul className="absolute z-50 w-full bg-white dark:bg-slate-900 border rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto left-0">
      {filteredClients.map((client) => (
        <li
          key={client.id}
          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer text-sm border-b last:border-0"
          onClick={() => handleSelectClient(client)}
        >
          <div className="font-medium">{client.name}</div>
          <div className="text-xs text-muted-foreground flex justify-between">
            <span>{client.phone}</span>
            <span>{client.city}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  useEffect(() => {
    const checkAvailability = async () => {
      if (checkInValue && checkOutValue) {
        setIsChecking(true);
        const checkInDate = new Date(checkInValue + "T00:00:00");
        const checkOutDate = new Date(checkOutValue + "T00:00:00");

        if (checkOutDate > checkInDate) {
          const available = await getAvailableChalesForPeriod(
            checkInDate,
            checkOutDate
          );
          setAvailableChales(available);
          setValue("chaletNumbers", []);
        } else {
          setAvailableChales([]);
        }
        setIsChecking(false);
      }
    };
    checkAvailability();
  }, [checkInValue, checkOutValue, setValue]);

  useEffect(() => {
    if (isOpen) {
      const newDefaultCheckOut = new Date(defaultCheckInDate);
      newDefaultCheckOut.setDate(defaultCheckInDate.getDate() + 1);
      reset({
        checkInDate: formatDateToYYYYMMDD(defaultCheckInDate),
        checkOutDate: formatDateToYYYYMMDD(newDefaultCheckOut),
        guestInfo: "",
        phoneInfo: "",
        chaletNumbers: [],
        depositDueDate: "",
        observations: "",
      });
      setAvailableChales(initialAvailableChales);
      setShowNameSuggestions(false);
      setShowPhoneSuggestions(false);
    }
  }, [isOpen, defaultCheckInDate, initialAvailableChales, reset]);

  const onSubmit = async (data) => {
    if (!data.chaletNumbers || data.chaletNumbers.length === 0) {
      toast.error("Por favor, selecione pelo menos um chalé.");
      return;
    }
    const checkIn = new Date(data.checkInDate + "T00:00:00");
    const checkOut = new Date(data.checkOutDate + "T00:00:00");
    if (checkOut <= checkIn) {
      toast.error("A data de Check-out deve ser posterior à de Check-in.");
      return;
    }

    await addClient({
      name: data.guestInfo,
      phone: data.phoneInfo,
      source: "reservation_modal",
    });

    const depositDueDate = data.depositDueDate
      ? new Date(data.depositDueDate + "T00:00:00")
      : null;

    data.chaletNumbers.forEach((chaletNum, index) => {
      const reservationData = {
        guestInfo: data.guestInfo,
        phoneInfo: data.phoneInfo,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        chaletNumber: parseInt(chaletNum, 10),
        depositDueDate: depositDueDate,
        observations: data.observations || "",
      };
      onAddReservation(reservationData);
    });

    onClose();
  };

  const allChalets = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Nova Reserva</DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 max-h-[80vh] overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* -- SEÇÃO: PERÍODO -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Período
              </h3>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="checkInDate">Check-in</Label>
                  <Input
                    type="date"
                    id="checkInDate"
                    {...register("checkInDate", { required: true })}
                    className="force-light-datepicker w-full min-w-[160px]"
                  />
                  {holidayNameCheckIn && (
                    <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                      <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                      {holidayNameCheckIn}
                    </div>
                  )}
                </div>

                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="checkOutDate">Check-out</Label>
                  <Input
                    type="date"
                    id="checkOutDate"
                    {...register("checkOutDate", { required: true })}
                    className="force-light-datepicker w-full min-w-[160px]"
                  />
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: CHALÉS -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Chalés Disponíveis
              </h3>

              {isChecking ? (
                <p className="text-sm text-muted-foreground">
                  Verificando disponibilidade...
                </p>
              ) : (
                <Controller
                  name="chaletNumbers"
                  control={control}
                  render={({ field }) => (
                    <div className="grid grid-cols-5 gap-2">
                      {allChalets.map((num) => {
                        const numStr = String(num);
                        const isAvailable = availableChales.includes(num);
                        const isSelected = field.value?.includes(numStr);

                        return (
                          <button
                            key={num}
                            type="button"
                            disabled={!isAvailable}
                            onClick={() => {
                              if (!isAvailable) return;
                              const current = field.value || [];
                              const next = isSelected
                                ? current.filter((v) => v !== numStr)
                                : [...current, numStr];
                              field.onChange(next);
                            }}
                            className={`
                              h-10 w-full rounded-md text-sm font-medium transition-colors border
                              ${
                                !isAvailable
                                  ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed border-transparent"
                                  : isSelected
                                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                  : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                              }
                            `}
                          >
                            {num < 10 ? `0${num}` : num}
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
              )}
              {availableChales.length === 0 && !isChecking && (
                <p className="text-sm text-destructive font-medium">
                  Nenhum chalé disponível para esta data.
                </p>
              )}
            </div>

            {/* -- SEÇÃO: HÓSPEDE -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Dados do Hóspede
              </h3>

              <div className="space-y-4">
                <div className="space-y-1 relative">
                  <Label htmlFor="guestInfo">Nome Completo</Label>
                  <Input
                    id="guestInfo"
                    {...register("guestInfo", {
                      required: "Nome é obrigatório",
                    })}
                    onFocus={() => {
                      setShowNameSuggestions(true);
                      setShowPhoneSuggestions(false);
                    }}
                    autoComplete="off"
                    placeholder="Buscar ou digitar nome..."
                  />
                  {showNameSuggestions && filteredClients.length > 0 && (
                    <SuggestionsList />
                  )}
                  {errors.guestInfo && (
                    <p className="text-xs text-destructive">
                      {errors.guestInfo.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1 relative">
                  <Label htmlFor="phoneInfo">Telefone</Label>
                  <Input
                    id="phoneInfo"
                    {...register("phoneInfo")}
                    onFocus={() => {
                      setShowPhoneSuggestions(true);
                      setShowNameSuggestions(false);
                    }}
                    autoComplete="off"
                    placeholder="(99) 99999-9999"
                  />
                  {showPhoneSuggestions && filteredClients.length > 0 && (
                    <SuggestionsList />
                  )}
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: DETALHES -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Detalhes
              </h3>

              <div className="space-y-4">
                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="depositDueDate">Vencimento do Depósito</Label>
                  <Input
                    type="date"
                    id="depositDueDate"
                    {...register("depositDueDate")}
                    className="force-light-datepicker w-full min-w-[160px]"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="observations">Observações</Label>
                  <Textarea
                    id="observations"
                    {...register("observations")}
                    className="resize-none"
                    placeholder="Ex: Chegará tarde, prefere andar térreo..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 sticky bottom-0 bg-background">
              <Button type="submit" size="lg" className="w-full sm:w-auto">
                Salvar Reserva
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddReservationModal;
